import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, BookOpen, Compass, Lightbulb, Sparkles, GraduationCap, Rocket, MapPin } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Container, Chip } from "@/components/nb";
import { Stagger, Item } from "@/components/motion";

const modules = [
  {
    to: "/study",
    key: "study",
    title: "Study & Doubts",
    tagline: "Diagnostic tests, doubt-clearing chat, 3D explainers.",
    color: "bg-sl-primary",
    icon: BookOpen,
    span: "md:col-span-8 md:row-span-2",
  },
  {
    to: "/career",
    key: "career",
    title: "Career Guidance",
    tagline: "Find your path. Talk to real mentors.",
    color: "bg-sl-secondary",
    icon: Compass,
    span: "md:col-span-4",
  },
  {
    to: "/ideas",
    key: "ideas",
    title: "Ideas & Innovation",
    tagline: "Turn ideas into real products. Win the monthly challenge.",
    color: "bg-sl-tertiary",
    icon: Lightbulb,
    span: "md:col-span-4",
  },
  {
    to: "/skills",
    key: "skills",
    title: "Skill Development",
    tagline: "Leadership. Communication. Time. Digital.",
    color: "bg-sl-accent",
    icon: Sparkles,
    span: "md:col-span-6",
  },
  {
    to: "/meghalaya",
    key: "meghalaya",
    title: "Meghalaya Explorer",
    tagline: "Every district — its problems, wins and initiatives.",
    color: "bg-sl-primary",
    icon: MapPin,
    span: "md:col-span-12",
  },
];

const stats = [
  { k: "Diagnostic subjects", v: "3" },
  { k: "Career paths", v: "6" },
  { k: "Skill tracks", v: "4" },
  { k: "AI doubt-buddy", v: "24/7" },
];

export default function Home() {
  const { user } = useAuth();
  return (
    <Container>
      {/* HERO */}
      <section className="grid grid-cols-1 md:grid-cols-12 gap-6 items-stretch relative">
        {/* Floating colorful blobs */}
        <motion.div
          aria-hidden
          animate={{ y: [0, -12, 0], rotate: [0, 10, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="pointer-events-none absolute -top-10 -left-6 w-24 h-24 rounded-full bg-sl-accent nb-border nb-shadow hidden md:block"
        />
        <motion.div
          aria-hidden
          animate={{ y: [0, 14, 0], rotate: [0, -8, 0] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
          className="pointer-events-none absolute -bottom-8 left-1/3 w-16 h-16 rounded-2xl bg-sl-secondary nb-border nb-shadow rotate-12 hidden md:block"
        />
        <motion.div
          aria-hidden
          animate={{ y: [0, -10, 0], x: [0, 6, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="pointer-events-none absolute top-6 right-1/2 w-10 h-10 rounded-full bg-sl-tertiary nb-border nb-shadow hidden md:block"
        />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="md:col-span-8 nb-border nb-shadow-xl rounded-2xl bg-white p-8 md:p-12 grain" data-testid="hero-block">
          <Chip color="tertiary">FOR STUDENTS · GRADES 6 → COLLEGE</Chip>
          <h1 className="mt-5 font-heading font-black tracking-tighter text-5xl md:text-7xl leading-[0.9]">
            Study smarter.<br />
            Find your <span className="bg-sl-primary px-2 nb-border rounded-lg">path.</span><br />
            Build your <span className="bg-sl-secondary px-2 nb-border rounded-lg">thing.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base md:text-lg text-neutral-700">
            One hub for diagnostic tests, AI doubt-clearing, career discovery, entrepreneurship and the skills school forgets to teach.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/study/diagnostic" data-testid="cta-diagnostic" className="px-6 py-3 rounded-lg nb-border nb-shadow nb-hover bg-black text-white font-heading font-bold uppercase text-sm">
              Take a diagnostic →
            </Link>
            <Link to="/career/assess" data-testid="cta-assess" className="px-6 py-3 rounded-lg nb-border nb-shadow nb-hover bg-sl-primary font-heading font-bold uppercase text-sm">
              Find my path
            </Link>
            {!user && (
              <Link to="/auth" data-testid="cta-signup" className="px-6 py-3 rounded-lg nb-border nb-shadow nb-hover bg-white font-heading font-bold uppercase text-sm">
                Create account
              </Link>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="md:col-span-4 grid grid-rows-2 gap-6">
          <div className="nb-border nb-shadow-lg rounded-2xl bg-sl-primary p-6 flex flex-col justify-between" data-testid="hero-stats">
            <div className="flex items-center justify-between">
              <span className="font-mono-sl text-xs uppercase tracking-widest">BY THE NUMBERS</span>
              <GraduationCap className="w-5 h-5" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              {stats.map((s) => (
                <div key={s.k} className="bg-white nb-border rounded-lg p-3">
                  <div className="font-heading text-3xl font-black tracking-tighter">{s.v}</div>
                  <div className="font-mono-sl text-[10px] uppercase mt-1">{s.k}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="nb-border nb-shadow-lg rounded-2xl bg-sl-secondary p-6 flex flex-col justify-between" data-testid="hero-quote">
            <span className="font-mono-sl text-xs uppercase tracking-widest">TODAY'S NUDGE</span>
            <div>
              <div className="font-heading font-black text-2xl md:text-3xl tracking-tight leading-tight">"You don't rise to your goals. You fall to your systems."</div>
              <div className="mt-3 font-mono-sl text-[11px] uppercase">— J. Clear</div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* MODULES BENTO */}
      <section className="mt-14">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-heading font-black text-3xl md:text-4xl tracking-tighter">Pick a hub.</h2>
          <span className="font-mono-sl text-xs uppercase text-neutral-500 hidden md:block">4 modules · one loop</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6" data-testid="modules-grid">
          {modules.map((m, idx) => {
            const Icon = m.icon;
            return (
              <motion.div
                key={m.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: idx * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className={`${m.span} md:col-span-6`}
              >
                <Link to={m.to} data-testid={`module-${m.key}`}
                  className={`${m.color} block nb-border nb-shadow-lg nb-hover-lg rounded-2xl p-8 h-full flex flex-col justify-between min-h-[220px]`}>
                  <div className="flex items-center justify-between">
                    <motion.div whileHover={{ rotate: -8, scale: 1.1 }} transition={{ type: "spring", stiffness: 300 }}>
                      <Icon className="w-9 h-9" />
                    </motion.div>
                    <ArrowUpRight className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-heading font-black tracking-tighter text-3xl md:text-4xl leading-tight">{m.title}</h3>
                    <p className="mt-2 text-neutral-800 max-w-md">{m.tagline}</p>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* WHY */}
      <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { t: "Made for the whole student.", d: "Not just marks. Also career, ideas, skills." },
          { t: "AI that teaches, not answers.", d: "Doubt-Buddy checks if you actually got it." },
          { t: "From knowing to doing.", d: "Every module ends with a real project." },
        ].map((x, i) => (
          <Item key={i}>
            <div className="bg-white nb-border rounded-xl p-6 nb-shadow h-full" data-testid={`why-${i}`}>
              <Rocket className="w-6 h-6 mb-3" />
              <h4 className="font-heading font-black text-xl tracking-tight">{x.t}</h4>
              <p className="text-neutral-700 mt-1">{x.d}</p>
            </div>
          </Item>
        ))}
      </Stagger>

      <footer className="mt-20 pt-8 border-t-2 border-black flex flex-wrap items-center justify-between gap-4">
        <div className="font-mono-sl text-xs uppercase tracking-widest">EDUMARKET · 2026</div>
        <div className="font-mono-sl text-xs uppercase text-neutral-500">Built for curious minds.</div>
      </footer>
    </Container>
  );
}
